import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Play } from "lucide-react";
import { formatDate, resolveImageUrl } from "@/lib/api";
import SmartCoverImage from "@/components/SmartCoverImage";

const CATEGORY_STYLES = ["bg-cat-mindmaps text-cat-mindmaps-text", "bg-cat-education text-cat-education-text", "bg-cat-books text-cat-books-text", "bg-cat-new-releases text-cat-new-releases-text"];

function catStyle(category) {
  if (!category) return "bg-ink/10 text-ink";
  let h = 0;
  for (let i = 0; i < category.length; i++) h = (h * 31 + category.charCodeAt(i)) >>> 0;
  return CATEGORY_STYLES[h % CATEGORY_STYLES.length];
}

export default function ArticleCard({ article, featured = false }) {
  if (!article) return null;
  const style = catStyle(article.category);
  const isBookCover = Boolean(article.category === "Books" && article.book_cover_url);
  const rawCover = isBookCover ? article.book_cover_url : article.cover_image_url;
  const cover = rawCover ? resolveImageUrl(rawCover) : null;
  const imgBg = isBookCover
    ? "linear-gradient(135deg, #f6f3ee 0%, #efe7d6 100%)"
    : undefined;
  const hasMedia = article.audio_url || article.video_url;
  // Excerpt: prefer the excerpt field; fall back to first 120 chars of plain-text content.
  const plainContent = String(article.content || article.body || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const excerpt = (article.excerpt || "").trim()
    || (plainContent ? plainContent.slice(0, 120) + (plainContent.length > 120 ? "…" : "") : "");

  if (featured) {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group col-span-1 lg:col-span-2 bg-white rounded-3xl overflow-hidden border border-ink/5 hover:shadow-[0_30px_70px_-30px_rgba(30,77,58,0.35)] transition-all duration-300 flex flex-col"
        data-testid={`article-card-featured-${article.slug}`}
      >
        <div className="relative aspect-[16/10] overflow-hidden" style={{ background: imgBg || "#f5f3ef" }}>
          {cover ? (
            <SmartCoverImage
              src={cover}
              alt={article.title}
              width="800"
              height="500"
              loading="lazy"
              decoding="async"
              className="w-full h-full group-hover:scale-105 transition-transform duration-700"
              style={{ padding: isBookCover ? '1.25rem' : 0 }}
            />
          ) : null}
          {article.category ? (
            <span className={`absolute top-5 left-5 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${style}`}>
              {article.category}
            </span>
          ) : null}
          {hasMedia ? (
            <span className="absolute top-5 right-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-brand text-[11px] font-medium">
              <Play className="w-3 h-3" /> {article.video_url ? "Video" : "Audio"}
            </span>
          ) : null}
        </div>
        <div className="p-6 sm:p-8 lg:p-9 flex-1 flex flex-col">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-ink-subtle inline-flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" /> Featured · {formatDate(article.published_at)}
          </div>
          <h3 className="mt-3 sm:mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight text-ink group-hover:text-brand transition-colors">
            {article.title}
          </h3>
          {article.book_author ? (
            <div className="mt-2 text-sm text-ink-muted">
              Book by <span className="font-medium text-ink">{article.book_author}</span>
            </div>
          ) : null}
          {excerpt ? (
            <p
              className="mt-3 line-clamp-3"
              style={{ fontSize: "0.875rem", color: "#4A5568", lineHeight: 1.6 }}
              data-testid="featured-excerpt"
            >
              {excerpt}
            </p>
          ) : null}
          <div className="pt-5 flex items-center justify-end">
            <span className="inline-flex items-center gap-1 text-sm font-medium text-brand">
              Read essay <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group bg-white rounded-2xl overflow-hidden border border-ink/5 hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(30,77,58,0.3)] transition-all duration-300 flex flex-col"
      data-testid={`article-card-${article.slug}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden" style={{ background: imgBg || "#f5f3ef" }}>
        {cover ? (
          <SmartCoverImage
            src={cover}
            alt={article.title}
            width="400"
            height="250"
            loading="lazy"
            decoding="async"
            className="w-full h-full group-hover:scale-105 transition-transform duration-700"
            style={{ padding: isBookCover ? '1rem' : 0 }}
          />
        ) : null}
        {article.category ? (
          <span className={`absolute top-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${style}`}>
            {article.category}
          </span>
        ) : null}
        {hasMedia ? (
          <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/90 text-brand text-[10px] font-medium">
            <Play className="w-3 h-3" /> {article.video_url ? "Video" : "Audio"}
          </span>
        ) : null}
      </div>
      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-ink-subtle inline-flex items-center gap-2">
          <Calendar className="w-3 h-3" /> {formatDate(article.published_at)}
        </div>
        <h3 className="mt-2.5 sm:mt-3 font-serif text-lg sm:text-xl lg:text-2xl leading-snug text-ink group-hover:text-brand transition-colors line-clamp-3">
          {article.title}
        </h3>
        {article.book_author ? (
          <div className="mt-1.5 text-xs sm:text-sm text-ink-muted line-clamp-1">
            Book by <span className="font-medium text-ink">{article.book_author}</span>
          </div>
        ) : null}
        <div className="mt-auto pt-4 sm:pt-5 flex items-center justify-end">
          <ArrowUpRight className="w-4 h-4 text-ink-subtle group-hover:text-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
        </div>
      </div>
    </Link>
  );
}
